import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AdditionalDetailsEditor from "./AdditionalDetailsEditor";
import AdditionalDetailsReadRows from "./AdditionalDetailsReadRows";
import { isCustomFieldsUnavailable } from "./api";
import { hasEditableChanges } from "./editableValue";
import ReassignAdditionalDetailsBlock from "./ReassignAdditionalDetailsBlock";
import recoverAdditionalDetailsDraft from "./recoverAdditionalDetailsDraft";
import useAdditionalDetails from "./useAdditionalDetails";
import useCustomFieldsCapability from "./useCustomFieldsCapability";
import useSessionPreference, { preferenceKey } from "./useSessionPreference";
import useSurfaceAcknowledgement from "./useSurfaceAcknowledgement";

const AuthorizedSection = ({
  teamId,
  leadId,
  generation,
  isAdmin,
  cleanupContext,
  onCleanupComplete,
  onBusyChange,
  onNavigationGuardChange,
  onSaved,
}) => {
  const [edit, setEdit] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const capability = useCustomFieldsCapability({
    teamId,
    generation,
    retainOnFocus: true,
    revalidateOnFocus: !edit,
  });
  const displayed = capability.variant === "displayed";
  const resource = useAdditionalDetails({
    teamId,
    leadId,
    generation,
    enabled: displayed && !accessDenied,
    revalidateOnFocus: !edit,
  });
  const [preferredExpanded, setExpanded] = useSessionPreference(
    preferenceKey(teamId, "profile-open"),
    true,
  );
  const expanded = !!cleanupContext || preferredExpanded;
  const busy = useRef(false);
  const pendingSave = useRef(null);
  const dataRef = useRef(null);
  const invalidateAccess = useCallback(() => {
    setAccessDenied(true);
    setEdit(null);
    dataRef.current = null;
    capability.reload();
  }, [capability.reload]);
  useEffect(() => {
    if (isCustomFieldsUnavailable(resource.error)) {
      invalidateAccess();
    }
  }, [resource.error, invalidateAccess]);
  const handleBusyChange = useCallback(
    (value) => {
      busy.current = value;
      onBusyChange?.(value);
    },
    [onBusyChange],
  );
  const canNavigate = useCallback(() => {
    if (busy.current) {
      return false;
    }
    const original = Object.fromEntries(
      (edit?.snapshot.values || []).map((row) => [
        row.definition_id,
        row.value,
      ]),
    );
    const dirty =
      edit &&
      hasEditableChanges(edit.snapshot.definitions, original, edit.draft);
    return (
      !dirty || window.confirm("Discard unsaved Additional Details changes?")
    );
  }, [edit]);
  useEffect(() => {
    onNavigationGuardChange?.(canNavigate);
    return () => onNavigationGuardChange?.(null);
  }, [canNavigate, onNavigationGuardChange]);
  const data = resource.data;
  const definitions = data?.definitions || [];
  useEffect(() => {
    if (resource.data) {
      dataRef.current = resource.data;
    }
  }, [resource.data]);
  useEffect(() => {
    if (!resource.data) {
      return;
    }
    setEdit((current) => {
      if (
        !current?.reloadRequestedFrom ||
        current.reloadRequestedFrom === resource.data
      ) {
        return current;
      }
      if (!resource.data.definitions.length) {
        return null;
      }
      const recovery = recoverAdditionalDetailsDraft(
        current.snapshot,
        current.draft,
        resource.data,
      );
      return {
        snapshot: resource.data,
        draft: recovery.draft,
        recovery,
        version: current.version + 1,
      };
    });
  }, [resource.data]);
  const rememberDraft = useCallback(
    (draft) =>
      setEdit((current) =>
        current && current.draft !== draft ? { ...current, draft } : current,
      ),
    [],
  );
  useSurfaceAcknowledgement({
    teamId,
    token: data?.surface_context,
    enabled: displayed && definitions.length > 0,
  });

  if (!displayed) {
    return null;
  }
  if (
    capability.loading ||
    resource.loading ||
    (edit?.reloadRequestedFrom && data && edit.snapshot !== data)
  ) {
    return (
      <section className="bt pt15" aria-label="Additional Details">
        <div className="mb15">
          <button
            type="button"
            className="btn btn-secondary btn-sm float-right"
            aria-label={
              expanded
                ? "Collapse Additional Details"
                : "Expand Additional Details"
            }
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
          >
            <FontAwesomeIcon
              icon={["far", expanded ? "minus" : "plus"]}
              aria-hidden="true"
            />
          </button>
          <h4 className="mb-0">Additional Details</h4>
        </div>
        {expanded && (
          <>
            <div
              role="status"
              className="alert alert-info d-flex align-items-center mb-3"
            >
              <span
                className="spinner-border spinner-border-sm text-success mr-2"
                aria-hidden="true"
              />
              Loading Additional Details.
            </div>
            <div aria-hidden="true" className="loading-skeleton">
              {["70%", "100%", "45%"].map((width) => (
                <p
                  key={width}
                  className="rounded mb-3"
                  style={{ width, height: 12 }}
                />
              ))}
            </div>
          </>
        )}
      </section>
    );
  }
  if (resource.error || accessDenied) {
    return (
      <div role="alert" className="py-3">
        Additional Details are temporarily unavailable.{" "}
        <button
          type="button"
          className="btn btn-link"
          onClick={() => {
            setAccessDenied(false);
            resource.reload();
          }}
        >
          Try Again
        </button>
      </div>
    );
  }
  if (!definitions.length && !cleanupContext) {
    return null;
  }
  const openEditor = () => {
    setExpanded(true);
    setEdit({
      snapshot: data,
      draft: Object.fromEntries(
        (data.values || []).map((row) => [row.definition_id, row.value]),
      ),
      version: 0,
    });
  };
  const reloadForReview = (draft) => {
    setEdit((current) => ({
      ...current,
      draft,
      reloadRequestedFrom: dataRef.current,
    }));
    resource.reload();
  };
  const save = async (changes) => {
    if (pendingSave.current) {
      return pendingSave.current;
    }
    const request = resource.save(changes, edit.snapshot);
    pendingSave.current = request;
    handleBusyChange(true);
    try {
      await request;
      setEdit(null);
      resource.reload();
    } catch (error) {
      if (isCustomFieldsUnavailable(error)) {
        invalidateAccess();
      }
      throw error;
    } finally {
      if (pendingSave.current === request) {
        pendingSave.current = null;
      }
      handleBusyChange(false);
    }
    if (changes.length) {
      onSaved?.(leadId);
    }
  };

  return (
    <section className="bt pt15" aria-label="Additional Details">
      <div className="d-flex align-items-start justify-content-between mb15">
        <h4 className="mb-0 mr-2">Additional Details</h4>
        <div className="d-flex align-items-center flex-shrink-0">
          <button
            type="button"
            className="btn btn-secondary btn-sm mr-1"
            aria-label={
              expanded
                ? "Collapse Additional Details"
                : "Expand Additional Details"
            }
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
            disabled={!!edit || !!cleanupContext}
          >
            <FontAwesomeIcon
              icon={["far", expanded ? "minus" : "plus"]}
              aria-hidden="true"
            />
          </button>
          {isAdmin && (
            <a
              className="btn btn-secondary btn-sm mr-1"
              aria-label="Manage Fields"
              title="Manage Fields"
              onClick={(event) => {
                if (!canNavigate()) {
                  event.preventDefault();
                }
              }}
              href="/team/edit?tab=contact_data&section=custom_fields"
            >
              <FontAwesomeIcon icon={["far", "cog"]} aria-hidden="true" />
            </a>
          )}
          {!cleanupContext &&
            !edit &&
            definitions.some((field) => field.editable) && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={openEditor}
              >
                <FontAwesomeIcon icon={["far", "edit"]} aria-hidden="true" />{" "}
                Edit
              </button>
            )}
        </div>
      </div>
      {expanded && (
        <>
          {cleanupContext && isAdmin && (
            <ReassignAdditionalDetailsBlock
              teamId={teamId}
              leadId={leadId}
              context={cleanupContext}
              onComplete={onCleanupComplete}
              onBusyChange={handleBusyChange}
            />
          )}
          {edit && (
            <AdditionalDetailsEditor
              key={edit.version}
              definitions={definitions}
              values={edit.snapshot.values || []}
              initialDraft={edit.draft}
              recovery={edit.recovery}
              onDraftChange={rememberDraft}
              onSave={save}
              onCancel={() => setEdit(null)}
              onReload={reloadForReview}
              onBusyChange={handleBusyChange}
            />
          )}
          {!edit && (
            <AdditionalDetailsReadRows
              definitions={definitions}
              values={data.values || []}
            />
          )}
        </>
      )}
    </section>
  );
};

const AdditionalDetailsSection = (props) => (
  <AuthorizedSection
    key={`${props.teamId}:${props.leadId}:${props.generation || 0}`}
    {...props}
    isAdmin={!!(props.isAdmin || Rails.helpers.currentUser?.admin)}
  />
);
const propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  generation: PropTypes.number,
  isAdmin: PropTypes.bool,
  cleanupContext: PropTypes.string,
  onCleanupComplete: PropTypes.func,
  onBusyChange: PropTypes.func,
  onNavigationGuardChange: PropTypes.func,
  onSaved: PropTypes.func,
};
AuthorizedSection.propTypes = propTypes;
AdditionalDetailsSection.propTypes = propTypes;
export default AdditionalDetailsSection;
